import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Cross, LayoutDashboard, Users, UserRound, CalendarDays, Megaphone, Images,
  Landmark, HandHeart, HeartHandshake, Church, FileText, Mail, ScrollText,
  Settings, LogOut, Menu, Globe, Wallet, Cake, ShieldCheck, FileBarChart, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";

export const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, module: "dashboard", section: "Main" },
  { to: "/admin/families", label: "Family Directory", icon: Users, module: "families", section: "People" },
  { to: "/admin/members", label: "Member Directory", icon: UserRound, module: "members", section: "People" },
  { to: "/admin/birthdays", label: "Birthdays", icon: Cake, module: "birthdays", section: "People" },
  { to: "/admin/events", label: "Events", icon: CalendarDays, module: "events", section: "Church" },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone, module: "announcements", section: "Church" },
  { to: "/admin/gallery", label: "Photo Gallery", icon: Images, module: "content", section: "Church" },
  { to: "/admin/history", label: "Church History", icon: Landmark, module: "content", section: "Church" },
  { to: "/admin/leadership", label: "Leadership", icon: HandHeart, module: "content", section: "Church" },
  { to: "/admin/ministries", label: "Ministries", icon: HeartHandshake, module: "content", section: "Church" },
  { to: "/admin/services", label: "Worship & Services", icon: Church, module: "content", section: "Church" },
  { to: "/admin/ledger", label: "Church Ledger", icon: Wallet, module: "ledger", section: "Administration" },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart, module: "reports", section: "Administration" },
  { to: "/admin/about", label: "About Content", icon: FileText, module: "content", section: "Administration" },
  { to: "/admin/messages", label: "Messages", icon: Mail, module: "messages", section: "Administration" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "full_admin"], section: "Administration" },
  { to: "/admin/access", label: "Access Management", icon: ShieldCheck, module: "access", section: "Administration" },
  { to: "/admin/settings", label: "Settings", icon: Settings, module: "settings", section: "Administration" },
];

const NAV_SECTIONS = ["Main", "People", "Church", "Administration"];

function NavLinks({ onNavigate }) {
  const { can, user } = useAuth();
  const visible = ADMIN_NAV.filter((item) =>
    item.roles ? item.roles.includes(user?.role) : can(item.module, "view")
  );
  return (
    <nav className="flex flex-col gap-3" aria-label="Admin navigation">
      {NAV_SECTIONS.map((section) => {
        const items = visible.filter((item) => item.section === section);
        if (items.length === 0) return null;
        return (
          <div key={section} className="flex flex-col gap-0.5">
            <p className="px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gold/80">{section}</p>
            {items.map((item) => {
              const link = (
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? "bg-gold text-navy" : "text-cream/85 hover:bg-cream/10 hover:text-cream"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              );
              return onNavigate ? <SheetClose asChild key={item.to}>{link}</SheetClose> : <div key={item.to}>{link}</div>;
            })}
          </div>
        );
      })}
    </nav>
  );
}

const INACTIVITY_MS = 10 * 60 * 1000;

// 10-minute inactivity auto-logout: only genuine user interaction (mouse,
// keyboard, touch, scroll) resets the timer — background API calls, polling
// and open-but-idle tabs do NOT. Interaction also sends a throttled heartbeat
// so the backend's server-side inactivity enforcement stays in sync.
function useInactivityLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const timer = useRef(null);
  const lastBeat = useRef(0);
  const throttle = useRef(0);

  useEffect(() => {
    if (!user) return undefined;
    let disposed = false;
    const onTimeout = async () => {
      if (disposed) return;
      await logout("inactivity");
      navigate("/admin/login", {
        replace: true,
        state: { error: "Your session expired due to inactivity. Please sign in again." },
      });
    };
    const onActivity = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(onTimeout, INACTIVITY_MS);
      const now = Date.now();
      if (now - lastBeat.current > 60000) {
        lastBeat.current = now;
        api.post("/auth/activity").catch(() => {});
      }
    };
    const handler = () => {
      const now = Date.now();
      if (now - throttle.current < 1000) return;
      throttle.current = now;
      onActivity();
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    onActivity();
    return () => {
      disposed = true;
      if (timer.current) clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, handler));
    };
  }, [user, logout, navigate]);
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);
  useInactivityLogout();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-secondary/50" data-testid="admin-layout">
      <aside className="hidden w-64 shrink-0 flex-col bg-navy grain-overlay lg:flex">
        <div className="flex items-center gap-3 border-b border-cream/10 px-5 py-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold text-navy">
            <Cross className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-bold text-cream">Christ Church CNI</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Admin Portal</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavLinks />
        </div>
        <div className="border-t border-cream/10 p-3 space-y-0.5">
          <Link to="/" className="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-cream/70 hover:bg-cream/10 hover:text-cream transition-colors" data-testid="admin-view-site">
            <Globe className="h-4 w-4" /> View Website
          </Link>
          <button onClick={() => setShowChangePw(true)} className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-cream/70 hover:bg-cream/10 hover:text-cream transition-colors" data-testid="admin-change-password-btn">
            <KeyRound className="h-4 w-4" /> Change Password
          </button>
          <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-cream/70 hover:bg-red-500/20 hover:text-red-200 transition-colors" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-border bg-white/80 px-4 py-3 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" data-testid="admin-mobile-menu" aria-label="Open admin menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" style={{ zIndex: 60 }} className="flex w-[86%] max-w-sm flex-col bg-navy p-0 text-cream">
                <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                <SheetDescription className="sr-only">Church admin portal sections and account actions</SheetDescription>
                <div className="flex items-center gap-3 border-b border-cream/10 px-5 py-5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold text-navy">
                    <Cross className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-heading text-sm font-bold text-cream">Christ Church CNI</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Admin Portal</p>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
                  <NavLinks onNavigate />
                </div>
                <div className="border-t border-cream/10 p-3 space-y-0.5">
                  <SheetClose asChild>
                    <Link to="/" className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-cream/85 hover:bg-cream/10 hover:text-cream transition-colors" data-testid="admin-view-site-mobile">
                      <Globe className="h-4 w-4" /> View Website
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <button onClick={() => setShowChangePw(true)} className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-cream/85 hover:bg-cream/10 hover:text-cream transition-colors" data-testid="admin-change-password-mobile">
                      <KeyRound className="h-4 w-4" /> Change Password
                    </button>
                  </SheetClose>
                  <SheetClose asChild>
                    <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm text-cream/85 hover:bg-red-500/20 hover:text-red-200 transition-colors" data-testid="admin-logout-mobile-drawer">
                      <LogOut className="h-4 w-4" /> Logout
                    </button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2 lg:hidden">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-navy text-cream">
                <Cross className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
              <span className="font-heading text-sm font-bold text-navy">Admin Portal</span>
            </div>
            <p className="hidden truncate text-sm text-muted-foreground lg:block">
              Signed in as <span className="font-semibold text-navy" data-testid="admin-user-email">{user?.email}</span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={doLogout} className="lg:hidden text-muted-foreground" data-testid="admin-logout-mobile">
            <LogOut className="h-4 w-4" />
          </Button>
        </header>
        <main className="min-w-0 flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog
        open={showChangePw}
        onOpenChange={setShowChangePw}
        onSuccess={async () => {
          await logout();
          navigate("/admin/login", { replace: true, state: { error: "Password changed. Please sign in with your new password." } });
        }}
      />
    </div>
  );
}
